import { Container, Divider } from "@material-ui/core";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  ArrayChip,
  CurrencyDisplay,
  CustomTable,
  DateTimeDisplay,
  ModalError,
  ModalSuccess,
  MultipleSelectInputWithTags,
  Page,
  PrimaryButton,
  StatusChip,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors } from "utils";

function InitCataloguePage() {
  const classes = globalStyles();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: "Bearer " + token,
  };
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [optionPublisher, setOptionPublisher] = useState([]);
  const [selectedPublisher, setSelectedPublisher] = useState([]);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [filterParams, setFilterParams] = useState({
    page: 1,
    size: 100,
    search: "",
  });
  const [hasMore, setHasMore] = useState(true);

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleChangeFilterParams = (value, key) => {
    setFilterParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleChangePublisher = (event, option) => {
    const { checked } = event?.target || false;
    setSelectedPublisher(prev =>
      checked
        ? [...prev, option]
        : prev.filter(item => item.publisher_id !== option.publisher_id)
    );
  };

  const getOptionPublisher = async () => {
    try {
      setLoadingFilter(true);
      const res = await axios.get(`${hardBaseUrl}/publisher`, {
        headers,
        params: filterParams,
      });
      const { data, meta } = res?.data || {};
      const newOptions = data?.map(item => ({
        ...item,
        id: item?.publisher_id,
      }));

      setOptionPublisher(prev => {
        const combined =
          filterParams.page === 1 ? newOptions : [...prev, ...newOptions];
        setHasMore(meta?.total > combined.length);
        return combined;
      });
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingFilter(false);
    }
  };
  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}/song-catalog/list`, {
        headers,
        params: {
          ...queryParams,
          ...(selectedPublisher && {
            publisher_ids: selectedPublisher
              .map(({ publisher_id }) => publisher_id)
              .toString(),
          }),
        },
      });
      const { data, meta } = res?.data;
      setDataTable(data || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const handleSubmit = async () => {
    try {
      setLoadingPage(true);
      await axios.post(
        `${hardBaseUrl}/song-catalog/init-song`,
        { publisher_ids: selectedPublisher?.map(id => id.publisher_id) },
        { headers }
      );
      ModalSuccess("Init Catalogue Successfully Processed").then(() =>
        getDataTable()
      );
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [selectedPublisher]
  );
  const debounceFilter = useCallback(
    debounce(() => {
      getOptionPublisher();
    }, 500),
    [filterParams]
  );

  useEffect(() => {
    const isSearching = Boolean(filterParams?.search);
    if (isSearching) {
      debounceFilter();
      return () => {
        debounceFilter.cancel();
      };
    } else {
      getOptionPublisher();
    }
  }, [filterParams, debounceFilter]);
  useEffect(() => {
    if (selectedPublisher?.length > 0) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, selectedPublisher]);

  return (
    <Page className={classes.root} title="Init Catalogue">
      <Container maxWidth={false}>
        <HeaderTitle title="Init Catalogue" breadcrumbData={breadcrumbData} />
        <Divider className={classes.divider} />
        <Grid container justifyContent="space-between" my="16px">
          <Grid item>
            <MultipleSelectInputWithTags
              textValue="Publisher"
              value={selectedPublisher}
              options={optionPublisher}
              optionKey="publisher_id"
              optionLabel="name"
              onChange={handleChangePublisher}
              search
              searchValue={filterParams?.search}
              onChangeSearch={value =>
                handleChangeFilterParams(value, "search")
              }
              setQueryParams={setFilterParams}
              hasMore={hasMore}
              loading={loadingFilter}
              customPaperWidth={500}
            />
          </Grid>
          <Grid item>
            <PrimaryButton label="Start" onClick={handleSubmit} />
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.size}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "size")
          }
          skeletonHeight="764px"
        />
      </Container>
    </Page>
  );
}

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Init Catalogue",
    active: true,
  },
];
const columnTable = [
  {
    name: "id",
    title: "Init ID",
  },
  {
    name: "created_at",
    title: "Date",
    renderText: item => <DateTimeDisplay date={item} />,
  },
  {
    name: "publishers",
    title: "Publisher",
    renderText: item => <ArrayChip list={item} max={5} />,
  },
  {
    name: "record_count",
    title: "Total Song",
    headerAlign: "right",
    renderText: item => <CurrencyDisplay value={item} />,
  },
  {
    name: "status",
    title: "Status",
    renderText: status => (
      <StatusChip
        label={status}
        type={
          status === "Finish"
            ? "success"
            : status === "Initialize" || status === "Running"
            ? "warning"
            : "danger"
        }
      />
    ),
  },
];
export default InitCataloguePage;
